import VueRouter from "vue-router";
import NewDraw from "@/views/draw/main.vue";
import { useDrawStore } from "@/store/draw";
import { DRAW_MODEL } from "@/constants/drawModel";
import { PAGE_TYPE } from "@/constants/pageType";
import { useMainStore } from "@/store/main";
import { oauthLogin } from "@/api/oauth";
import Encyclopedias from "@/views/encyclopedias/index.vue";
import PestSearch from "@/views/encyclopedias/search.vue";
import PestSubClass from "@/views/encyclopedias/subClass.vue";
import PestDetail from "@/views/encyclopedias/detail.vue";
import EmptyView from "@/views/EmptyView.vue";
import { devInfo } from "@/utils/device";
import * as dd from 'dingtalk-jsapi';

// 地图相关的页面逻辑处理
// 根据传递的 page_type 跳转到对应的页面
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleDrawBeforeEnter = (to: any, from: any, next: any) => {
  const pageType = +to.query.page_type;

  router.getRoutes().forEach((route) => {
    if (
      route.meta &&
      route.meta.pageType &&
      route.meta.pageType.includes(pageType)
    ) {
      next({
        path: route.path,
        query: to.query,
      });
    }
  });
};

export interface IRouteMeta {
  // 页面标题
  title?: string;
  // 禁用缓存，页面默认有缓存 （keep-alive），注意禁用缓存的页面在页面内部必须配置 name
  disableCache?: boolean;
}

const router: VueRouter = new VueRouter({
  mode: "hash",
  routes: [
    // 地图相关页面对外的 path
    {
      path: "/draw",
      name: "draw",
      beforeEnter: handleDrawBeforeEnter,
    },
    // draw -> drawInner
    {
      path: "/drawInner",
      component: NewDraw,
      name: "drawInner",
      meta: {
        title: "圈一圈地",
      },
      children: [
        // 绘制
        {
          path: "draw",
          component: () => import("@/views/draw/pages/DrawPage.vue"),
          meta: {
            title: "圈一圈地",
            pageType: [PAGE_TYPE.drawing],
            drawModel: DRAW_MODEL.readToCreate,
          },
        },
        // 编辑
        {
          path: "edit",
          component: () => import("@/views/draw/pages/EditPage.vue"),
          meta: {
            title: "圈一圈地",
            pageType: [PAGE_TYPE.edit],
            drawModel: DRAW_MODEL.update,
          },
        },
        // 首页
        {
          path: "home",
          component: () => import("@/views/draw/pages/HomePage.vue"),
          meta: {
            title: "",
            pageType: [PAGE_TYPE.home],
            drawModel: DRAW_MODEL.pureRead,
          },
        },
        // 量地赚钱
        {
          path: "enclosure",
          component: () => import("@/views/draw/pages/EnclosurePage.vue"),
          meta: {
            title: "量地赚钱",
            pageType: [PAGE_TYPE.enclosure],
            drawModel: DRAW_MODEL.readToCreate,
          },
        },
        // 出租赚钱
        {
          path: "rent",
          component: () => import("@/views/draw/pages/RentPage.vue"),
          meta: {
            title: "出租赚钱",
            pageType: [PAGE_TYPE.rent, PAGE_TYPE.rentFirstShowList],
            drawModel: DRAW_MODEL.readToCreate,
          },
        },
        // 我的种养
        {
          path: "myPlanting",
          component: () => import("@/views/draw/pages/PlantingPage.vue"),
          meta: {
            title: "我的种养",
            pageType: [PAGE_TYPE.planting],
            drawModel: DRAW_MODEL.read,
          },
        },
        // 天气
        {
          path: "weather",
          component: () => import("@/views/draw/pages/WeatherPage.vue"),
          meta: {
            title: "天气预报",
            pageType: [PAGE_TYPE.weather],
            drawModel: DRAW_MODEL.navigationAndMaps,
          },
        },
      ],
    },
    {
      path: "/pest",
      component: EmptyView,
      meta: {
        title: "病害虫百科",
      },
      children: [
        {
          path: "index",
          component: Encyclopedias,
          name: "pest",
          meta: {
            title: "病害虫首页",
            disableCache: false,
          },
        },
        {
          path: "pestSearch",
          component: PestSearch,
          name: "pestSearch",
          meta: {
            title: "病害虫百科搜索",
            disableCache: true,
          },
        },
        {
          path: "pestSubClass",
          component: PestSubClass,
          name: "pestSubClass",
          meta: {
            title: "病害虫百科",
            disableCache: true,
          },
        },
        {
          path: "pestDetail",
          component: PestDetail,
          name: "pestDetail",
          meta: {
            title: "病害虫详情",
            disableCache: true,
          },
        },
      ],
    },
    {
      path: "/agreement",
      component: () => import("@/views/agreement/index.vue"),
      meta: {
        title: "隐私协议",
      },
    },
  ],
});

// 全局公共数据处理
router.beforeEach(async (to, from, next) => {
  const mainStore = useMainStore();

  if(to.fullPath.indexOf('pest')>-1){
    if(devInfo.isDingTalkDevice){
      dd.biz.navigation.setTitle({
        title : '病虫害百科',//控制标题文本，空字符串表示显示默认文本
      });
    }
  }

  if (to.query) {
    const query = to.query;
    if (query.token) {
      mainStore.token = query.token as string;
    }
    // 钉钉数字乡村
    if (query.ticket && !mainStore.token) {
      const [err, res] = await oauthLogin({
        auto_regist: "true",
        grant_type: "ding_ticket",
        ticket: query.ticket as string,
      });
      if (!err && res !== undefined) {
        // 获取token
        mainStore.token = res.access_token;
      }
    }
  }

  if (to.meta) {
    const meta = to.meta;
    if (meta.title) {
      mainStore.pageTitle = to.meta!.title;
    }
  }

  next();
});

// 地图相关页面公共数据处理
router.beforeEach((to, from, next) => {
  if (to.matched && to.matched.length && to.matched[0].name === "drawInner") {
    const drawStore = useDrawStore();
    const mainStore = useMainStore();

    // 地图相关页面内跳转，重置初始数据
    drawStore.$reset();


    // if (mainStore.userLocation) {
    //   drawStore.mapCenter = mainStore.userLocation;
    // }

    if (to.query) {
      const query = to.query;
      if (query.page_type) {
        drawStore.queryPageType = +query.page_type;
      }
    }

    // 用户位置
    if (to.query && to.query.lat && to.query.lng) {
      drawStore.mapCenter = [+to.query.lat, +to.query.lng];
      mainStore.userLocation = [+to.query.lat, +to.query.lng];
      mainStore.getUserLocationName([+to.query.lat, +to.query.lng]);
    }

    if (to.meta) {
      const meta = to.meta;
      if (meta.pageType) {
        drawStore.pageType = meta.pageType[0];
      }
      if (meta.drawModel) {
        drawStore.drawModel = meta.drawModel;
      }
    }
  }

  next();
});

export default router;
